import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker, useLocation } from "react-router-dom";
import { CensusChangesWarningDialog } from "../components/DraftMode/CensusChangesWarningDialog";
import { NavigationWarningDialog } from "../components/DraftMode/NavigationWarningDialog";
import { performGlobalRefreshCore } from "../components/header/hooks";
import { resetScheduleAfterDraft } from "../hooks/scheduleCacheWrites";
import { useInvalidateCallIns } from "../hooks/useInvalidateCallIns";
import { useFeatureFlag } from "../hooks/useOpenFeatureFlag";
import { usePublishChanges } from "../hooks/usePublishChanges";
import { backendService } from "../services/backend.service";
import { localStorageService } from "../services/LocalStorageService";
import { deepCloneObject } from "../utils/dateUtils";
import {
	ChangesSource,
	ChangeType,
	type DayItem,
	type DraftChange,
	DraftChangeType,
	type HospitalData,
	type NurseInShift,
	type PublishRequestBody,
	type ShiftChange,
} from "../utils/models";
import { setDraftModeActive } from "../utils/networkWrapper";
import { useAuth } from "./AuthProvider";
import { useAnalytics } from "./analytics/hooks";
import { DraftModeContext } from "./DraftModeContext";
import { FEATURE_FLAGS } from "./FeatureFlagsProvider";
import { useHospitalData } from "./HospitalDataProvider";
import { useNotificationsContext } from "./NotificationsContext";
import { useRequestsContext } from "./RequestsContext";
import { useSnackbar } from "./SnackbarContext";
import { useWeekIndex } from "./WeekIndexProvider";
import { useWeekViewContext } from "./WeekView/WeekViewContext";

export const DraftModeProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const location = useLocation();
	const { weekIndex } = useWeekIndex();
	const { weekViewData } = useWeekViewContext();
	const {
		hospitalData,
		setHospitalData,
		chosenDepartment,
		getShiftDate,
		showErrorSnackbar,
		refreshDepartmentData,
		refreshDepartmentWageData,
		refreshNursesData,
	} = useHospitalData();
	const { refreshRequests } = useRequestsContext();
	const { fetchNotifications } = useNotificationsContext();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const { mutateAsync: publishChangesAsync, isPending: isPublishing } = usePublishChanges();
	const invalidateCallIns = useInvalidateCallIns();
	const queryClient = useQueryClient();
	const refreshWageBreakdown = useCallback(async () => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: ["shift-wage-breakdown"] }),
			queryClient.invalidateQueries({ queryKey: ["pto-costs"] }),
			queryClient.invalidateQueries({ queryKey: ["nurse-overtime"] }),
			refreshDepartmentWageData(),
		]);
	}, [queryClient, refreshDepartmentWageData]);
	const {
		userInfo: { id: userId },
	} = useAuth();

	const [isDraftMode, setIsDraftMode] = useState<boolean>(false);
	const [draftChanges, setDraftChanges] = useState<DraftChange[]>([]);
	const [originalData, setOriginalData] = useState<HospitalData>();
	const [originalWeekViewData, setOriginalWeekViewData] = useState<DayItem[]>([]);
	const [locallyApprovedRequests, setLocallyApprovedRequests] = useState<string[]>([]);
	const [showCensusWarning, setShowCensusWarning] = useState<boolean>(false);
	const [pendingPublishData, setPendingPublishData] = useState<{
		censusUpdates: Array<{ date: string; shift_type: string; override_value: number; note?: string; source: ChangesSource }>;
		censusDeletes: Array<{ date: string; shift_type: string; source: ChangesSource }>;
		newCensus: number | undefined;
	} | null>(null);

	const isDraftModeEnabled = useFeatureFlag(FEATURE_FLAGS.DraftModeEnabled);

	const activeDayData = useMemo<DayItem | undefined>(() => {
		if (!isDraftModeEnabled || !weekViewData.length) return;

		const match = location.pathname.match(/day-schedule\/(\d{4}-\d{2}-\d{2})/);
		if (!match) return;

		// Match by date — weekViewData is keyed by dayIndex, not ISO weekday.
		const targetDate = DateTime.fromISO(match[1]);
		return weekViewData.find((d) => d.date.hasSame(targetDate, "day"));
	}, [location.pathname, weekViewData, isDraftModeEnabled]);

	const hasUnsavedChanges = useMemo<boolean>(
		() => !!draftChanges.length || !!locallyApprovedRequests.length,
		[draftChanges.length, locallyApprovedRequests.length],
	);

	const blocker = useBlocker(({ currentLocation, nextLocation }) => isDraftMode && currentLocation.pathname !== nextLocation.pathname);

	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => isDraftMode && e.preventDefault();
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [isDraftMode]);

	const calculateDraftChanges = useCallback((): DraftChange[] => {
		if (!isDraftModeEnabled || !isDraftMode || !originalWeekViewData.length || !originalData || !activeDayData) return [];

		// Invariant: `originalWeekViewData` is a 7-entry array indexed by Sunday-anchored weekdayISO
		// (0=Sun..6=Sat). If `weekViewData`'s ordering ever shifts (different week start day, or
		// non-7-day weeks), this index lookup must be replaced with a date-based `.find()`.
		const activeDayIndex = activeDayData.date.weekdayISO;
		const changes: DraftChange[] = [];
		const originalDayData = originalWeekViewData[activeDayIndex];
		if (!originalDayData) return [];

		const currentNurses = activeDayData.shifts;
		const originalNurses = originalDayData.shifts;

		const calculateDiff = (original: NurseInShift, current: NurseInShift): Partial<NurseInShift> | null => {
			const diff: Partial<NurseInShift> = {};
			let hasChanges = false;

			if (original.charge !== current.charge) {
				diff.charge = current.charge;
				hasChanges = true;
			}
			if (original.trainingStatus !== current.trainingStatus) {
				diff.trainingStatus = current.trainingStatus;
				hasChanges = true;
			}
			if (original.bonus !== current.bonus) {
				diff.bonus = current.bonus;
				hasChanges = true;
			}
			if (original.location !== current.location) {
				diff.location = current.location;
				hasChanges = true;
			}
			if (original.notes !== current.notes) {
				diff.notes = current.notes;
				hasChanges = true;
			}
			if (original.startTime?.toISO() !== current.startTime?.toISO()) {
				diff.startTime = current.startTime;
				hasChanges = true;
			}
			if (original.endTime?.toISO() !== current.endTime?.toISO()) {
				diff.endTime = current.endTime;
				hasChanges = true;
			}

			return hasChanges ? diff : null;
		};

		currentNurses.forEach((currentNurse) => {
			const originalNurse = originalNurses.find((n) => n.id === currentNurse.id);

			if (!originalNurse) {
				if (currentNurse.isPendingToDelete) return;
				if (currentNurse.requestId) {
					changes.push({
						id: `${DraftChangeType.APPROVE_ADD_REQUEST}_${Date.now()}_${Math.random()}`,
						type: DraftChangeType.APPROVE_ADD_REQUEST,
						timestamp: DateTime.now(),
						payload: {
							nurse: currentNurse,
							requestId: currentNurse.requestId,
							weekIndex,
							shiftIndex: 0,
							dayIndex: activeDayIndex,
						},
					});
				} else {
					changes.push({
						id: `${DraftChangeType.ADD}_${Date.now()}_${Math.random()}`,
						type: DraftChangeType.ADD,
						timestamp: DateTime.now(),
						payload: {
							nurse: currentNurse,
							weekIndex,
							shiftIndex: 0,
							dayIndex: activeDayIndex,
							location: currentNurse.location,
						},
					});
				}
			} else {
				if (currentNurse.isPendingToDelete && !originalNurse.isPendingToDelete) {
					changes.push({
						id: `${DraftChangeType.REMOVE}_${currentNurse.id}_${Date.now()}`,
						type: DraftChangeType.REMOVE,
						timestamp: DateTime.now(),
						payload: {
							nurse: currentNurse,
							weekIndex,
							shiftIndex: 0,
							dayIndex: activeDayIndex,
							message: currentNurse.message || "",
						},
					});
				} else {
					const diff = calculateDiff(originalNurse, currentNurse);

					if (diff) {
						changes.push({
							id: `${DraftChangeType.UPDATE}_${currentNurse.id}_${Date.now()}`,
							type: DraftChangeType.UPDATE,
							timestamp: DateTime.now(),
							payload: {
								nurse: currentNurse,
								weekIndex,
								shiftIndex: 0,
								dayIndex: activeDayIndex,
								diff,
							},
						});
					}
				}
			}
		});

		originalNurses.forEach((originalNurse) => {
			const stillExists = currentNurses.find((n) => n.id === originalNurse.id);

			if (!stillExists) {
				if (originalNurse.replacedWith) {
					const replacementNurse = currentNurses.find((n) => n.nurseId === originalNurse.replacedWith);
					if (replacementNurse) {
						changes.push({
							id: `${DraftChangeType.APPROVE_REPLACE_REQUEST}_${Date.now()}_${Math.random()}`,
							type: DraftChangeType.APPROVE_REPLACE_REQUEST,
							timestamp: DateTime.now(),
							payload: {
								nurseToRemove: originalNurse,
								nurse: replacementNurse,
								requestId: replacementNurse.requestId,
								weekIndex,
								shiftIndex: 0,
								dayIndex: activeDayIndex,
							},
						});
						return;
					}
				}
				changes.push({
					id: `${DraftChangeType.REMOVE}_${Date.now()}_${Math.random()}`,
					type: DraftChangeType.REMOVE,
					timestamp: DateTime.now(),
					payload: {
						nurseToRemove: originalNurse,
						weekIndex,
						shiftIndex: 0,
						dayIndex: activeDayIndex,
						message: originalNurse.message || "",
					},
				});
			}
		});

		const currentCensus = activeDayData.dayStats.census;
		const originalCensus = originalDayData.dayStats.census;

		if (currentCensus !== originalCensus) {
			const currentDay = hospitalData.weeks[weekIndex]?.weeklyShifts[0]?.days[activeDayIndex];
			const currentNote = currentDay?.census_override?.note;
			const date = currentDay?.date.toFormat("yyyy-MM-dd");
			const shiftType = hospitalData.weeks[weekIndex]?.weeklyShifts[0]?.type;

			if (currentCensus !== undefined && date && shiftType) {
				changes.push({
					id: `${DraftChangeType.UPDATE_CENSUS}_${Date.now()}_${Math.random()}`,
					type: DraftChangeType.UPDATE_CENSUS,
					timestamp: DateTime.now(),
					payload: {
						censusOverride: currentCensus,
						censusNote: currentNote,
						date,
						shiftType,
						weekIndex,
						shiftIndex: 0,
						dayIndex: activeDayIndex,
						source: ChangesSource.DaySchedule,
					},
				});
			} else if (currentCensus === undefined && originalCensus !== undefined && date && shiftType) {
				changes.push({
					id: `${DraftChangeType.DELETE_CENSUS_OVERRIDE}_${Date.now()}_${Math.random()}`,
					type: DraftChangeType.DELETE_CENSUS_OVERRIDE,
					timestamp: DateTime.now(),
					payload: {
						date,
						shiftType,
						weekIndex,
						shiftIndex: 0,
						dayIndex: activeDayIndex,
						source: ChangesSource.DaySchedule,
					},
				});
			}
		}

		return changes;
	}, [isDraftModeEnabled, isDraftMode, originalWeekViewData, originalData, activeDayData, weekIndex, hospitalData]);

	useEffect(() => {
		if (isDraftMode) setDraftChanges(calculateDraftChanges());
	}, [isDraftMode, calculateDraftChanges]);

	const enableDraftMode = useCallback((): void => {
		track("draft_mode_enabled");
		showSnackbar(t("draft.mode.enabled"), "info");
		setOriginalData(deepCloneObject(hospitalData));
		setOriginalWeekViewData(deepCloneObject(weekViewData));
		setIsDraftMode(true);
		setDraftChanges([]);
		setDraftModeActive(true);
	}, [hospitalData, weekViewData, showSnackbar, t, track]);

	const disableDraftMode = useCallback((): void => {
		track("draft_mode_disabled");
		showSnackbar(t("draft.mode.disabled"), "info");
		setIsDraftMode(false);
		setDraftChanges([]);
		setOriginalData(undefined);
		setOriginalWeekViewData([]);
		setLocallyApprovedRequests([]);
		setDraftModeActive(false);

		localStorageService.resetClientGroupedRefreshData(userId);
	}, [showSnackbar, t, track, userId]);

	const discardChanges = useCallback((): void => {
		track("draft_changes_discarded", { change_count: draftChanges.length, approved_requests: locallyApprovedRequests.length });
		showSnackbar(t("draft.mode.changes.discarded", { count: draftChanges.length + locallyApprovedRequests.length }), "info");
		setDraftChanges([]);
		setLocallyApprovedRequests([]);
		setIsDraftMode(false);
		setDraftModeActive(false);
		if (originalData) setHospitalData(originalData);
		// Drafts are edits to the cached weeks and never reach the server, so reloading the weeks discards them.
		void resetScheduleAfterDraft(queryClient, chosenDepartment.department_id);
		setOriginalData(undefined);
		setOriginalWeekViewData([]);
	}, [originalData, draftChanges.length, locallyApprovedRequests.length, setHospitalData, showSnackbar, t, track, queryClient, chosenDepartment.department_id]);

	const executePublish = useCallback(
		async (
			censusUpdates: Array<{ date: string; shift_type: string; override_value: number; note?: string; source: ChangesSource }>,
			censusDeletes: Array<{ date: string; shift_type: string; source: ChangesSource }>,
		): Promise<void> => {
			try {
				const changes: ShiftChange[] = [];
				const acceptedRequests: Record<string, number[]> = {};
				const declinedRequests: Record<string, number[]> = {};

				for (const draftChange of draftChanges) {
					const { nurse, weekIndex, shiftIndex, dayIndex, location, message, requestId, nurseToRemove, diff } = draftChange.payload;

					let changeType: ChangeType;
					const nurseData: NurseInShift | undefined = nurse ? { ...nurse } : undefined;

					switch (draftChange.type) {
						case DraftChangeType.ADD:
							changeType = ChangeType.Insert;
							if (nurseData) {
								nurseData.location = location;
								nurseData.isPending = false;
							}
							break;

						case DraftChangeType.REMOVE:
							changeType = ChangeType.Delete;
							if (nurseData) nurseData.message = message;
							break;

						case DraftChangeType.UPDATE:
							changeType = ChangeType.Update;
							if (nurseData && diff) Object.assign(nurseData, diff);
							break;

						case DraftChangeType.APPROVE_ADD_REQUEST:
							changeType = ChangeType.Insert;
							if (nurseData) {
								nurseData.isPending = false;
								nurseData.location = location;
							}
							if (requestId && nurseData) {
								acceptedRequests[requestId] ??= [];
								acceptedRequests[requestId].push(nurseData.nurseId);
							}
							break;

						case DraftChangeType.APPROVE_REPLACE_REQUEST:
							if (nurseToRemove) {
								changes.push({
									nurse: { ...nurseToRemove },
									shiftIndex,
									shiftType: nurseToRemove.shiftType,
									weekIndex,
									dayIndex,
									date: getShiftDate(weekIndex, shiftIndex, dayIndex),
									departmentId: nurseToRemove.departmentId,
									changeType: ChangeType.Delete,
								});
							}
							changeType = ChangeType.Insert;

							if (nurseData) {
								nurseData.location = location;
								nurseData.isPending = false;
							}
							if (requestId && nurseData) {
								acceptedRequests[requestId] ??= [];
								acceptedRequests[requestId].push(nurseData.nurseId);
							}
							break;

						case DraftChangeType.UPDATE_CENSUS:
						case DraftChangeType.DELETE_CENSUS_OVERRIDE:
							continue;

						default:
							changeType = ChangeType.Update;
					}

					if (nurseData) {
						changes.push({
							nurse: nurseData,
							shiftIndex,
							shiftType: nurseData.shiftType,
							weekIndex,
							dayIndex,
							date: getShiftDate(weekIndex, shiftIndex, dayIndex),
							departmentId: nurseData.departmentId,
							changeType,
						});
					}
				}

				const updateRequestBody: PublishRequestBody = {
					accepted_requests: acceptedRequests,
					declined_requests: declinedRequests,
					actual_changes: [],
					changes,
					source: ChangesSource.DaySchedule,
				};

				setDraftModeActive(false);
				await Promise.all([
					publishChangesAsync(updateRequestBody),
					...censusUpdates.map((censusUpdate) =>
						backendService.createOrUpdateCensusOverride({ department_id: chosenDepartment.department_id, ...censusUpdate }),
					),
					...censusDeletes.map((censusDelete) => backendService.deleteCensusOverride({ department_id: chosenDepartment.department_id, ...censusDelete })),
				]);

				// A Draft Mode removal is only a local, unpersisted edit until this publish runs —
				// `useHandleNurseChange`'s own call-in invalidation skips itself in draft mode for exactly
				// that reason — so this is the one place a published draft removal needs it. Gated on the
				// batch actually containing a removal so an add/update-only publish doesn't refetch a cache
				// it never touched.
				if (changes.some((change) => change.changeType === ChangeType.Delete)) {
					void invalidateCallIns();
				}

				track("draft_changes_published", { change_count: draftChanges.length, approved_requests: locallyApprovedRequests.length });
				showSnackbar(t("draft.mode.published", { count: draftChanges.length + locallyApprovedRequests.length }), "success");

				await performGlobalRefreshCore({
					refreshDepartmentData,
					refreshNursesData,
					refreshRequests,
					fetchNotifications,
					refreshWageBreakdown,
					chosenDepartmentId: chosenDepartment.department_id,
					timezone: chosenDepartment.timezone,
					userId,
				});
				// The cached weeks still hold the drafts as local edits; reloading them shows what was saved.
				void resetScheduleAfterDraft(queryClient, chosenDepartment.department_id);

				disableDraftMode();
			} catch (error) {
				setDraftModeActive(true);
				showErrorSnackbar(t("draft.mode.publish.failed"), { change_count: draftChanges.length }, error);
				trackError("draft_changes_publish_failed", {
					change_count: draftChanges.length,
					approved_requests: locallyApprovedRequests.length,
					error: String(error),
				});
			}
		},
		[
			draftChanges,
			locallyApprovedRequests,
			chosenDepartment,
			getShiftDate,
			disableDraftMode,
			refreshDepartmentData,
			refreshNursesData,
			refreshRequests,
			fetchNotifications,
			refreshWageBreakdown,
			showSnackbar,
			showErrorSnackbar,
			track,
			t,
			trackError,
			publishChangesAsync,
			invalidateCallIns,
			userId,
			queryClient,
		],
	);

	const publishChanges = useCallback(async (): Promise<void> => {
		if (!draftChanges.length && !locallyApprovedRequests.length) return showSnackbar(t("draft.mode.no.changes"), "warning");

		const censusUpdates: Array<{ date: string; shift_type: string; override_value: number; note?: string; source: ChangesSource }> = [];
		const censusDeletes: Array<{ date: string; shift_type: string; source: ChangesSource }> = [];

		for (const draftChange of draftChanges) {
			const { censusOverride, censusNote, date, shiftType, source } = draftChange.payload;

			if (draftChange.type === DraftChangeType.UPDATE_CENSUS) {
				if (date && shiftType && censusOverride !== undefined && source) {
					censusUpdates.push({ date, shift_type: shiftType, override_value: censusOverride, note: censusNote, source: source as ChangesSource });
				}
			} else if (draftChange.type === DraftChangeType.DELETE_CENSUS_OVERRIDE) {
				if (date && shiftType && source) {
					censusDeletes.push({ date, shift_type: shiftType, source: source as ChangesSource });
				}
			}
		}

		if (censusUpdates.length || censusDeletes.length) {
			const newCensus = censusUpdates.length > 0 ? censusUpdates[0].override_value : undefined;
			setPendingPublishData({ censusUpdates, censusDeletes, newCensus });
			setShowCensusWarning(true);
		} else {
			await executePublish(censusUpdates, censusDeletes);
		}
	}, [draftChanges, locallyApprovedRequests, showSnackbar, t, executePublish]);

	const handleCensusWarningConfirm = useCallback(async (): Promise<void> => {
		setShowCensusWarning(false);
		if (pendingPublishData) {
			await executePublish(pendingPublishData.censusUpdates, pendingPublishData.censusDeletes);
			setPendingPublishData(null);
		}
	}, [pendingPublishData, executePublish]);

	const handleCensusWarningCancel = useCallback((): void => {
		setShowCensusWarning(false);
		setPendingPublishData(null);
	}, []);

	const handleCancelNavigation = useCallback((): void => {
		if (blocker.state === "blocked") blocker.reset();
	}, [blocker]);

	const handleConfirmLeave = useCallback((): void => {
		if (blocker.state === "blocked") {
			discardChanges();
			blocker.proceed();
		}
	}, [blocker, discardChanges]);

	const handlePublishAndLeave = useCallback(async (): Promise<void> => {
		if (blocker.state === "blocked") {
			await publishChanges();
			blocker.proceed();
		}
	}, [blocker, publishChanges]);

	const isRequestLocallyApproved = useCallback((requestId: string): boolean => locallyApprovedRequests.includes(requestId), [locallyApprovedRequests]);

	return (
		<DraftModeContext.Provider
			value={{
				isDraftMode,
				draftChanges,
				hasUnsavedChanges,
				originalData,
				enableDraftMode,
				disableDraftMode,
				publishChanges,
				discardChanges,
				isPublishing,
				locallyApprovedRequests,
				isRequestLocallyApproved,
				activeDayData,
			}}
		>
			{children}
			<NavigationWarningDialog
				open={blocker.state === "blocked"}
				onCancel={handleCancelNavigation}
				onConfirmLeave={handleConfirmLeave}
				onPublishAndLeave={handlePublishAndLeave}
				changeCount={draftChanges.length + locallyApprovedRequests.length}
				isPublishing={isPublishing}
			/>
			<CensusChangesWarningDialog
				open={showCensusWarning}
				onCancel={handleCensusWarningCancel}
				onConfirm={handleCensusWarningConfirm}
				newCensus={pendingPublishData?.newCensus}
			/>
		</DraftModeContext.Provider>
	);
};
